/**
 * Styles da tela /acompanhamento (Fase 5).
 *
 * Reusa primitives de pages/Orcamentos/styles (Card, Table, Pill, etc) e
 * adiciona FilterChip + OriginBadge proprios. table-layout fixed + ellipsis
 * para zero scroll lateral.
 */
import styled, { css } from "styled-components";

import { mq } from "../../styles/canonical/breakpoints";
import { getToneTokens } from "../../styles/shared";
import { kpiFaixaCss, kpiTileCss } from "../../components/ListaCompacta/styles";

// Reexporta primitives de Orcamentos — DRY
export {
  Card,
  SectionTitle,
  FilterBar,
  SearchInput,
  Select,
  PrimaryButton,
  SecondaryButton,
  IconButton,
  Pill,
  EmptyMsg,
  ErrorMsg,
  InlineHint,
  TableWrapper,
  Table,
  Field,
  FieldLabel,
  FieldRow,
  FieldValue,
  Block,
} from "../Orcamentos/styles";

/**
 * Chip de filtro rapido — single-select. Usado para alternar entre
 * "Tudo / Atrasados / Urgentes / Por etapa". Mostra contador inline.
 */
export const FilterChip = styled.button`
  background: ${({ theme, $active, $tone }) => {
    if (!$active) return "transparent";
    const t = getToneTokens(theme, $tone || "info");
    return t.background;
  }};
  color: ${({ theme, $active, $tone }) => {
    if (!$active) return theme.textSecondary;
    const t = getToneTokens(theme, $tone || "info");
    return t.color;
  }};
  border: 1px solid ${({ theme, $active, $tone }) => {
    if (!$active) return theme.border;
    const t = getToneTokens(theme, $tone || "info");
    return t.border;
  }};
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  transition: background 0.12s ease, border-color 0.12s ease;

  &:hover {
    background: ${({ theme, $active }) =>
      $active ? undefined : theme.surfaceAlt};
  }

  @media ${mq.tablet} {
    min-height: 36px;
    flex: 0 0 auto;
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.4;
      cursor: not-allowed;
    `}
`;

export const ChipCount = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 8px;
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.textPrimary};

  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
  }
`;

/**
 * Badge pequeno indicando origem do dado: "Planilha" (legado operacional)
 * ou "Backend" (criado via PharuERP). Usa tone neutro pra nao competir com
 * Pill de etapa/prioridade.
 */
export const OriginBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.textSecondary};
  border: 1px solid ${({ theme }) => theme.border};

  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
    letter-spacing: 0;
    padding: 0 5px;
  }
`;

/**
 * Container da lista de chips (responsivo, wrap).
 */
export const ChipBar = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  padding: 8px 0;
  border-top: 1px dashed ${({ theme }) => theme.border};
  border-bottom: 1px dashed ${({ theme }) => theme.border};
  margin: 4px 0 8px;

  /* Celular/tablet: uma linha rolável em vez de 3 linhas de chips. */
  @media ${mq.tablet} {
    flex-wrap: nowrap;
    overflow-x: auto;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
    gap: 6px;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

/**
 * Celula truncavel com tooltip (pattern do Orcamentos). Aplica white-space
 * nowrap + overflow ellipsis. Use junto com `title={...}` no TD.
 */
export const TruncCell = styled.span`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`;

/**
 * Texto auxiliar abaixo de uma celula (ex: "ha 12 dias" debaixo da etapa).
 */
export const SubText = styled.span`
  display: block;
  font-size: 0.72rem;
  color: ${({ theme }) => theme.textSecondary};
  margin-top: 1px;

  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
  }
`;

/* Busca + "Ocultar entregues" — saiu do style inline para aceitar @media. */
export const FiltroLinha = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;

  @media ${mq.tablet} {
    flex-wrap: nowrap;

    > input[type="search"] {
      flex: 1 1 auto;
      min-width: 0;
    }
  }
`;

export const OcultarEntregues = styled.label`
  display: flex;
  gap: 6px;
  align-items: center;
  font-size: 0.85rem;

  @media ${mq.tablet} {
    flex: 0 0 auto;
    min-height: 36px;
    white-space: nowrap;
    font-size: var(--text-min, 12px);

    input {
      width: 18px;
      height: 18px;
    }
  }
`;

/* Badge de prioridade na célula "Número" (antes Pill com fontSize inline). */
export const PrioridadeMini = styled.span`
  display: inline-flex;
  font-size: 0.62rem;

  && > * {
    font-size: inherit;
  }

  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
  }
`;

/* Timeline do DrillPanel: 7 etapas. No desktop mantém o layout antigo;
   abaixo de 1024px quebra em linhas (4 por linha) em vez de rolar de lado. */
export const Timeline = styled.div`
  display: flex;
  gap: 2px;
  align-items: stretch;
  margin-top: 8px;
  overflow-x: auto;

  @media ${mq.tablet} {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 4px;
    overflow-x: hidden;
  }
`;

export const TimelinePasso = styled.div`
  flex: 1;
  min-width: 70px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 8px 4px;
  border-radius: 6px;
  background: ${({ $atual }) => ($atual ? "rgba(0,0,0,0.04)" : "transparent")};
  border: 1px solid ${({ $atual }) => ($atual ? "rgba(0,0,0,0.1)" : "transparent")};

  .marca {
    font-size: 0.65rem;
  }
  .nome {
    font-size: 0.68rem;
    text-align: center;
    color: ${({ $atual }) => ($atual ? "inherit" : "rgba(0,0,0,0.5)")};
    font-weight: ${({ $atual }) => ($atual ? 600 : 400)};
    line-height: 1.1;
  }

  @media ${mq.tablet} {
    min-width: 0;
    padding: 6px 2px;

    .marca,
    .nome {
      font-size: var(--text-min, 12px);
    }
    .nome {
      overflow-wrap: anywhere;
    }
  }
`;

/* ---- Cabeçalho e KPIs compactos (celular/tablet) — redesenho mobile 2026-09-24 ---- */
export const CabecalhoCompacto = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

export const TituloCompacto = styled.h1`
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  font-size: 17px;
  line-height: 1.2;
  font-weight: 700;
  font-family: var(--font-display);
  color: ${({ theme }) => theme.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  small {
    display: block;
    font-size: var(--text-min, 12px);
    font-weight: 500;
    color: ${({ theme }) => theme.textTertiary};
  }
`;

export const BotaoIcone = styled.button`
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textSecondary};
  cursor: pointer;
`;

export const KpiFaixa = styled.div`
  display: flex;
  ${kpiFaixaCss}
`;

export const KpiTile = styled.div`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  ${kpiTileCss}

  .kpi-num {
    color: ${({ theme, $tone = "neutral" }) =>
      $tone === "neutral" || $tone === "default" ? theme.textPrimary : getToneTokens(theme, $tone).text};
  }
  .kpi-label {
    color: ${({ theme }) => theme.textTertiary};
    text-transform: uppercase;
  }
`;

/* Texto com tom de estado dentro da meta do card (no lugar de uma Pill). */
export const MetaTom = styled.b`
  color: ${({ theme, $tone = "neutral" }) =>
    $tone === "neutral" || $tone === "default" ? theme.textPrimary : getToneTokens(theme, $tone).text} !important;
`;
